import { Col, Form, Row } from 'react-bootstrap';
import { FormWithValidation } from 'shared/form';
import { FormLabel, FormLabelTitle } from '../FormLabel';
import {
  ABM_CENTRO_COSTO,
  CONSULTA_CENTRO_COSTO,
  CONSULTA_TRANSFERENCIA,
  DEVOLUCION_PARCIAL,
  DEVOLUCION_TOTAL,
  DISTRIBUCION,
  FUNCIONALIDADES,
} from 'user/constants/Funcionalidad';
import { useEffect } from 'react';
import { RegisterOptions, useController } from 'react-hook-form';
import { TipoUsuario } from 'user/models/Usuario';
import { FuncionalidadesSelectorSkeleton } from './FuncionalidadesSelectorSkeleton';
import { useUserService } from 'user/services/useUserService';
import { useDataFetcher } from 'shared/http';
import { Funcionalidades } from 'user/models/Funcionalidades';

interface FuncionalidadesSelectorProps {
  tipoUsuario: TipoUsuario;
  name: string;
  registerOptions?: RegisterOptions<any, any>;
}

export const applyBusinessRules = (values: Set<string>, currentCode: string) => {
  // Exclusiones
  if (currentCode === DEVOLUCION_TOTAL) values.delete(DEVOLUCION_PARCIAL);
  if (currentCode === DEVOLUCION_PARCIAL) values.delete(DEVOLUCION_TOTAL);

  // Dependencias
  if (
    values.has(DISTRIBUCION) ||
    values.has(DEVOLUCION_TOTAL) ||
    values.has(DEVOLUCION_PARCIAL)
  ) {
    values.add(CONSULTA_TRANSFERENCIA);
  }

  if (values.has(ABM_CENTRO_COSTO)) {
    values.add(CONSULTA_CENTRO_COSTO);
  }
};

export const FuncionalidadesSelector = ({
  name,
  registerOptions,
  tipoUsuario,
}: FuncionalidadesSelectorProps) => {
  const [funcionalidades, setRequest] = useDataFetcher<Funcionalidades[]>();
  const userService = useUserService();
  const {
    field: { value, onChange },
    fieldState: { error, invalid },
  } = useController({
    name,
    rules: registerOptions,
    defaultValue: [],
  });

  useEffect(() => {
    if (tipoUsuario) {
      setRequest(() => userService.findFuncionalidades(tipoUsuario));
    }
  }, [userService, setRequest, tipoUsuario]);

  const handleCheckChange = (codigo: string, isChecked: boolean) => {
    const currentSet = new Set<string>(value || []);

    if (isChecked) {
      currentSet.add(codigo);
      applyBusinessRules(currentSet, codigo);
    } else {
      currentSet.delete(codigo);
    }

    onChange(Array.from(currentSet));
  };

  if (funcionalidades.isLoading) {
    return <FuncionalidadesSelectorSkeleton rows={funcionalidades.data?.length || 5} />;
  }

  return (
    <>
      {funcionalidades.data?.length > 0 && (
        <>
          <Row>
            <Col>
              {funcionalidades.data?.map((item, index) => {
                const isChecked = (value || []).includes(item.codigo);

                return (
                  <Row key={`${item.codigo}-${index}`} className="mb-4">
                    <Col md="1" style={{ width: '4.333333%' }}>
                      <Form.Check
                        data-testid={`check-${item.codigo}`}
                        id={`check-${item.codigo}`}
                        type="checkbox"
                        checked={isChecked}
                        isInvalid={invalid}
                        required={invalid}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleCheckChange(item.codigo, e.target.checked)
                        }
                      />
                    </Col>
                    <Col>
                      <FormLabelTitle>{item.detalle}</FormLabelTitle>
                      <FormLabel>{FUNCIONALIDADES[item.codigo]}</FormLabel>
                    </Col>
                  </Row>
                );
              })}
            </Col>
          </Row>
          {error && (
            <FormWithValidation.Control.Feedback
              type="invalid"
              controlId="funcionalidades"
            >
              {error.message}
            </FormWithValidation.Control.Feedback>
          )}
        </>
      )}
    </>
  );
};
